import { maintainArmsRaceLoadouts } from '../rules/armsRace';
import { ArmsRacePlayer } from '../traits';
import { createQuery, type Entity, type World } from 'koota';
import { Vec2 } from 'planck';
import { emit, getContext } from '../context';
import { rising } from '../input';
import { createBoxBody, registerBody, assignNetId } from '../physics/bodies';
import {
  Aim,
  Combat,
  Controller,
  Dead,
  Held,
  HeldBy,
  Loose,
  NetId,
  OwnedBy,
  Player,
  PrevTransform,
  Projectile,
  ProjectileKind,
  Transform,
  Weapon,
} from '../traits';
import type { WeaponDef } from './schema';
import { weaponByIndex, weaponIndex } from './defs';
import { SWING_TICKS } from './projectiles';
import { raycastClosest } from '../physics/queries';
import { combatAllowed } from '../rules/rounds';
import { weaponMuzzle, weaponPoint } from './geometry';
import { resolveWeaponAnchor } from './pose';
import { partyWeaponPool } from '../rules/party';
import { PartyState } from '../traits';

/** Blink dagger: hop this far along the aim, stopping short of the first wall in the way. */
const BLINK_DISTANCE = 4;

const holders = createQuery(Player, Aim, Combat, Transform);
const looseWeapons = createQuery(Weapon, Loose, Transform);

function kindId(kind: WeaponDef['projectile']['kind']): number {
  switch (kind) {
    case 'bullet':
      return ProjectileKind.Bullet;
    case 'pellets':
      return ProjectileKind.Pellet;
    case 'grenade':
      return ProjectileKind.Grenade;
    case 'rocket':
      return ProjectileKind.Rocket;
    case 'beam':
      return ProjectileKind.Beam;
    case 'melee':
      return ProjectileKind.Melee;
    case 'field':
      return ProjectileKind.Field;
    case 'creature':
      return ProjectileKind.Creature;
    case 'burst-into':
      return ProjectileKind.BurstInto;
    case 'return-disc':
      return ProjectileKind.ReturnDisc;
    case 'siphon':
      return ProjectileKind.Siphon;
    default:
      return ProjectileKind.Bullet;
  }
}

function spawnBullet(
  world: World,
  owner: Entity,
  def: WeaponDef,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  extraSpread = 0,
  launcherNetId = 0,
): void {
  const ctx = getContext(world);
  const spread = ((def.projectile.spreadDeg + extraSpread) * Math.PI) / 180;
  const angle = Math.atan2(dirY, dirX) + (ctx.rng.next() - 0.5) * spread;
  const speed = def.projectile.speed;
  const vx = Math.cos(angle) * speed;
  const vy = Math.sin(angle) * speed;
  const proj = world.spawn(
    Projectile({
      kind: kindId(def.projectile.kind),
      damage: def.projectile.damage,
      speed,
      bounces: def.projectile.bounce,
      fuse:
        def.projectile.kind === 'melee'
          ? SWING_TICKS
          : def.projectile.kind === 'rocket' && def.projectile.fuse === 0
            ? 180
            : def.projectile.fuse,
      x,
      y,
      vx,
      vy,
      gravity: def.projectile.gravity * (ctx.settings.mutators.includes('low-gravity') ? 0.55 : 1),
      ownerGrace: ctx.tuning.ownerGraceTicks,
      defId: weaponIndex(def.id),
      spawnTick: ctx.tick,
      creditStage: owner.get(ArmsRacePlayer)?.stage ?? -1,
      launcherNetId,
    }),
    OwnedBy(owner),
  );
  assignNetId(world, proj);
  const needsBody =
    def.projectile.kind === 'grenade' ||
    def.projectile.kind === 'rocket' ||
    def.projectile.kind === 'field' ||
    def.projectile.kind === 'creature' ||
    def.projectile.kind === 'burst-into';
  if (needsBody) {
    // `projectile.radius` is the blast / field reach, not the shell: the body is a fist-sized
    // object that rides through gaps and bounces like a grenade should.
    const half =
      def.projectile.kind === 'field' ? 0.2 : def.projectile.burstInto === 'spike' ? 0.22 : 0.14;
    const body = createBoxBody(ctx.physics, proj, 'projectile', x, y, half, half, 'dynamic', {
      density: 0.4,
      friction: 0.2,
      restitution: def.projectile.bounce > 0 ? 0.55 : 0.05,
      bullet: true,
      fixedRotation: false,
    });
    body.setLinearVelocity(new Vec2(vx, vy));
    body.setGravityScale(
      def.projectile.gravity > 0
        ? (def.projectile.gravity * (ctx.settings.mutators.includes('low-gravity') ? 0.55 : 1)) /
            ctx.tuning.gravity
        : 0,
    );
    registerBody(world, proj, body);
  }
}

export function spawnWeapon(
  world: World,
  defId: string,
  x: number,
  y: number,
  loose = true,
): Entity {
  const ctx = getContext(world);
  let def = weaponByIndex(weaponIndex(defId));
  const selected = world.get(PartyState)?.weaponDef ?? -1;
  if (selected >= 0) def = weaponByIndex(selected);
  else if (ctx.settings.mutators.includes('melee-only') && def.projectile.kind !== 'melee') {
    def = partyWeaponPool(ctx.settings)[0] ?? weaponByIndex(weaponIndex('fists'));
  }
  const entity = world.spawn(
    Weapon({
      defId: weaponIndex(def.id),
      ammo: def.ammo,
      pickupCooldown: 0,
      thrown: false,
      thrownHit: false,
    }),
    Transform({ x, y, angle: 0 }),
  );
  assignNetId(world, entity);
  if (loose) entity.add(Loose());
  if (loose && ctx.settings.mode === 'arms-race') ctx.pendingDestroy.push(entity);
  const profile = def.throwProfile;
  const body = createBoxBody(ctx.physics, entity, 'weapon', x, y, profile?.halfWidth ?? 0.28, profile?.halfHeight ?? 0.12, 'dynamic', {
    density: profile?.density ?? 0.35,
    friction: profile?.friction ?? 0.4,
    restitution: 0.1,
    fixedRotation: profile?.upright ?? false,
    bullet: !!profile,
  });
  registerBody(world, entity, body);
  emit(world, {
    type: 'spawn',
    kind: `weapon:${def.id}`,
    netId: entity.get(Transform) ? ctx.ids.peek() - 1 : 0,
  });
  return entity;
}

function heldWeapon(world: World, player: Entity): Entity | undefined {
  for (const weapon of world.query(Weapon, Held)) {
    if (weapon.targetFor(HeldBy) === player) return weapon;
  }
  return undefined;
}

/** Manual throws and empty-gun flings use the same origin, ownership and hit reset. */
function releaseWeapon(world: World, player: Entity, weapon: Entity): void {
  const ctx = getContext(world);
  const aim = player.get(Aim);
  const transform = player.get(Transform);
  const state = weapon.get(Weapon);
  const body = ctx.bodies.get(player);
  const weaponBody = ctx.bodies.get(weapon);
  if (!aim || !transform || !state || !body || !weaponBody) return;
  const def = weaponByIndex(state.defId);
  const profile = def.throwProfile;
  let x = transform.x + aim.x * 0.6;
  let y = transform.y + aim.y * 0.6;
  if (profile) {
    const wall = raycastClosest(world, transform.x, transform.y, x, y, (hit) => hit.kind !== 'solid' && hit.kind !== 'prop');
    if (wall) {
      const clearance = Math.max(profile.halfWidth, profile.halfHeight) + 0.025;
      x = wall.x + wall.nx * clearance;
      y = wall.y + wall.ny * clearance;
    }
  }
  const velocity = body.getLinearVelocity();
  weapon.remove(Held);
  weapon.remove(HeldBy('*'));
  weapon.remove(OwnedBy('*'));
  weapon.add(Loose(), OwnedBy(player));
  weapon.set(Weapon, {
    ...state,
    thrown: true,
    thrownHit: false,
    pickupCooldown: ctx.tuning.pickupCooldownTicks,
  });
  // Hit detection runs before physics sync. Never leave a thrown hit at the old pickup position.
  const release = { x, y, angle: profile?.upright ? 0 : weaponBody.getAngle() };
  weapon.set(Transform, release);
  weapon.set(PrevTransform, release);
  weaponBody.setPosition(new Vec2(x, y));
  weaponBody.setAngle(release.angle);
  weaponBody.setActive(true);
  if (profile) weaponBody.setAngularVelocity(profile.spin * (aim.x < 0 ? -1 : 1));
  const speed = profile?.speed ?? ctx.tuning.throwSpeed;
  weaponBody.setLinearVelocity(
    new Vec2(
      velocity.x + aim.x * speed,
      velocity.y + aim.y * speed,
    ),
  );
  emit(world, { type: 'throw', player, weaponId: def.id });
}

export function weapons(world: World): void {
  const ctx = getContext(world);
  maintainArmsRaceLoadouts(world);
  const live = combatAllowed(world);

  world.query(looseWeapons).updateEach(([weapon]) => {
    if (weapon.pickupCooldown > 0) weapon.pickupCooldown -= 1;
  });

  world.query(holders).updateEach(([player, aim, combat, transform], entity) => {
    if (entity.has(Dead)) return;
    const input = ctx.inputs[player.inputIndex] ?? ctx.inputs[player.slot];
    const prev = ctx.prevInputs[player.inputIndex] ?? ctx.prevInputs[player.slot];
    if (!input) return;

    let held = heldWeapon(world, entity);
    if (!held && ctx.settings.mode !== 'arms-race') {
      world.query(looseWeapons).updateEach(([wep, wt], weaponEntity) => {
        if (held) return;
        if (wep.pickupCooldown > 0) return;
        const dx = wt.x - transform.x;
        const dy = wt.y - transform.y;
        if (dx * dx + dy * dy < 0.85 * 0.85) {
          weaponEntity.remove(Loose);
          weaponEntity.remove(OwnedBy('*'));
          weaponEntity.add(Held(), HeldBy(entity));
          const def = weaponByIndex(wep.defId);
          // Mutate the updateEach view so Koota writeback keeps the refill.
          wep.ammo = ctx.tuning.refillOnPickup && !def.throwProfile && def.id !== 'siphon' ? def.ammo : wep.ammo;
          wep.thrown = false;
          wep.thrownHit = false;
          wep.pickupCooldown = 0;
          const body = ctx.bodies.get(weaponEntity);
          body?.setActive(false);
          held = weaponEntity;
          emit(world, { type: 'pickup', player: entity, weaponId: def.id });
        }
      });
    }

    if (!held) return;
    const wep = held.get(Weapon);
    if (!wep) return;
    const def = weaponByIndex(wep.defId);
    const body = ctx.bodies.get(entity);
    if (!body) return;

    if (ctx.settings.mode !== 'arms-race' && rising(prev?.throw ?? false, input.throw)) {
      releaseWeapon(world, entity, held);
      return;
    }

    if (combat.blocking || combat.stun > 0 || !live) return;
    const fireEdge = rising(prev?.attack ?? false, input.attack);
    const fireHeld = input.attack;
    const canFire =
      (def.fireMode === 'semi' && fireEdge) ||
      (def.fireMode === 'auto' && fireHeld) ||
      (def.fireMode === 'burst' && fireEdge) ||
      (def.fireMode === 'hold' && fireHeld);
    const cd = ctx.fireCd.get(entity) ?? 0;
    if (cd > 0) ctx.fireCd.set(entity, cd - 1);
    if (!canFire || (ctx.fireCd.get(entity) ?? 0) > 0) return;
    if (def.projectile.kind === 'return-disc') {
      const launcher = held.get(NetId)?.id;
      // Availability is derived from the live projectile, so cleanup cannot strand a cooldown flag.
      if ([...world.query(Projectile)].some((projectile) => {
        const shot = projectile.get(Projectile)!;
        return shot.kind === ProjectileKind.ReturnDisc && shot.launcherNetId === launcher;
      })) return;
    }
    if (!def.infiniteAmmo && wep.ammo <= 0) {
      if (ctx.tuning.flingWhenEmpty) {
        releaseWeapon(world, entity, held);
      }
      return;
    }

    ctx.fireCd.set(entity, def.fireIntervalTicks);
    if (def.throwProfile?.attackThrows) {
      releaseWeapon(world, entity, held);
      return;
    }
    if (def.id === 'blink-dagger') {
      // Teleport first so the swing that follows lands where the fighter reappears: stop short of walls,
      // and short of the first enemy on the line so the blade arrives in range.
      const wall = raycastClosest(
        world,
        transform.x,
        transform.y,
        transform.x + aim.x * BLINK_DISTANCE,
        transform.y + aim.y * BLINK_DISTANCE,
        (h) =>
          h.entity === entity || (h.kind !== 'solid' && h.kind !== 'prop' && h.kind !== 'player'),
      );
      const dist = wall
        ? Math.max(0, wall.fraction * BLINK_DISTANCE - (wall.kind === 'player' ? 0.7 : 0.4))
        : BLINK_DISTANCE;
      const px = transform.x + aim.x * dist;
      const py = transform.y + aim.y * dist;
      body.setPosition(new Vec2(px, py));
      body.setLinearVelocity(new Vec2(aim.x * 3, Math.max(0, aim.y * 3)));
      entity.set(Transform, { x: px, y: py, angle: transform.angle });
      transform.x = px;
      transform.y = py;
      emit(world, { type: 'explosion', x: px, y: py, radius: 0.6, damage: 0 });
    }
    const shots =
      def.projectile.kind === 'pellets'
        ? def.projectile.count
        : def.fireMode === 'burst'
          ? (def.burstCount ?? 3)
          : 1;
    const anchor = resolveWeaponAnchor(world, entity, def, {
      ...transform,
      aimX: aim.x,
      aimY: aim.y,
      ducking: entity.get(Controller)?.ducking ?? false,
      twoHanded: def.twoHanded,
      height: ctx.tuning.height,
    });
    const { x: muzzleX, y: muzzleY } = weaponPoint(
      weaponMuzzle(def.shape.kind, def.shape.length),
      anchor,
    );
    for (let i = 0; i < shots; i++) {
      spawnBullet(world, entity, def, muzzleX, muzzleY, aim.x, aim.y, 0, held.get(NetId)?.id ?? 0);
    }
    if (def.id === 'office-keyboard') {
      emit(world, { type: 'office-debris', x: muzzleX, y: muzzleY, item: def.id });
    }
    if (!def.infiniteAmmo) {
      held.set(Weapon, { ...wep, ammo: wep.ammo - 1 });
    }
    const vel = body.getLinearVelocity();
    body.setLinearVelocity(
      new Vec2(
        vel.x - aim.x * def.recoil.back + aim.x * def.recoil.forward,
        vel.y + def.recoil.up - aim.y * def.recoil.back,
      ),
    );
    emit(world, {
      type: 'shot',
      source: entity,
      weaponId: def.id,
      x: muzzleX,
      y: muzzleY,
      aimX: aim.x,
      aimY: aim.y,
    });
  });
}
